import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { OrderProps, OrdersData, Page } from '../../types';
import { ColumnDef } from 'features/common/hooks/useColumnSelection';
import { formatDate, getStatusTagBackground } from './utils';
import { TRANSACTION_TYPE } from './constants';
import {
	PunchupBreakdownCell,
	PunchupDataRow,
	PunchupDateTimeCell,
	PunchupEntityCell,
	PunchupNameCell,
	PunchupTableEmptyRow,
	PunchupTableLoadingRow,
} from 'features/common/components/Punchup/primitives/table/PunchupTable';
import { PunchupLineItemList } from 'features/common/components/Punchup/primitives/display/PunchupLineItemList';
import { PunchupTag } from 'features/common/components/Punchup/primitives/display/PunchupTag';
import { PunchupTicketTypeTag } from 'features/common/components/Punchup/primitives/display/PunchupTicketTypeTag';
import { useTicketTypeColors } from 'features/common/hooks/useTicketTypeColors';
import { countTicketsByType } from 'features/common/utils/compedTickets';
import styles from './PunchupTableBody.module.css';

interface PunchupTableBodyProps extends OrderProps {
	visibleDefs: ColumnDef[];
	/** True while the initial fetch is in flight (data === undefined). */
	isLoading?: boolean;
	/** True when a search term or filter is active — switches the empty body to "no results" copy. */
	hasActiveQuery?: boolean;
}

export const PunchupTableBody: FC<PunchupTableBodyProps> = ({
	ordersQueryObject,
	visibleDefs,
	isLoading,
	hasActiveQuery,
}) => {
	const { t } = useTranslation();
	const { data: orders } = ordersQueryObject;
	// Resolve the type colour from the shared source (the event's ticket types), not the per-row
	// `ticket_type_color` — the /orders payload null-fills it, which is what showed grey tags here.
	const { resolveByName } = useTicketTypeColors();

	const realStatus = (order: OrdersData): string => {
		if (order.transaction_type === TRANSACTION_TYPE.REGISTRATION && order.status === 'Completed') return 'Registered';
		if (order.transaction_type === TRANSACTION_TYPE.VOID && order.status === 'Refunded') return 'Voided';
		return order.status;
	};

	const renderCol = (col: ColumnDef, order: OrdersData) => {
		const status = realStatus(order);
		switch (col.key) {
			case 'date':
				return <PunchupDateTimeCell key="date">{formatDate(order.date_time)}</PunchupDateTimeCell>;
			case 'name':
				return (
					<PunchupEntityCell key="name" title={order.User?.full_name}>
						{order.User?.full_name}
					</PunchupEntityCell>
				);
			case 'status':
				return (
					<td key="status">
						<PunchupTag size="sm" label={status} background={getStatusTagBackground(status)} />
					</td>
				);
			case 'ticketType': {
				// Per-type count breakdown (one colour-tagged line per type). No price line —
				// a per-type net subtotal cannot reconcile against the all-in Amount (NetPrice
				// already folds in fees, taxes and add-ons; docs/transactions_business_logic.md
				// §3), so money lives only in the Amount column. Same count helper as the Comped
				// table — both show count-only.
				return (
					<PunchupBreakdownCell key="ticketType">
						<PunchupLineItemList
							items={Object.entries(countTicketsByType(order.tickets, order.tickets_name)).map(([name, count]) => ({
								label: <PunchupTicketTypeTag name={name} color={resolveByName(name)} />,
								sortKey: name,
								count,
							}))}
						/>
					</PunchupBreakdownCell>
				);
			}
			case 'email':
				return (
					<PunchupNameCell key="email" title={order.User?.email}>
						{order.User?.email}
					</PunchupNameCell>
				);
			case 'phone':
				return (
					<PunchupNameCell key="phone" title={order.User?.phone_number}>
						{order.User?.phone_number || '-'}
					</PunchupNameCell>
				);
			case 'price': {
				const isNonCharging =
					order.transaction_type === TRANSACTION_TYPE.REGISTRATION || order.transaction_type === TRANSACTION_TYPE.VOID;
				const hasPrice = order.price !== undefined && order.price !== null;
				return (
					<td key="price" className={styles.amountCell}>
						{isNonCharging ? 'registration' : hasPrice ? `$${order.price.toFixed(2)}` : ''}
					</td>
				);
			}
			case 'event':
				return (
					<td key="event">{order.events_name?.length > 1 ? order.events_name.join(', ') : order.events_name?.[0]}</td>
				);
			case 'quantity':
				return <td key="quantity">{order.quantity}</td>;
			case 'addons':
				// One line per add-on: "N× name" — identity + count only, no dollar figure.
				// The all-in Amount (NetPrice) already includes add-on revenue, so a per-add-on
				// $ line double-counts money already in Amount; the count itself is also
				// backend-inflated until the aggregation fix lands (G1/G7). See
				// docs/transactions_business_logic.md §3–§4. Dash when none.
				return (
					<PunchupBreakdownCell key="addons">
						{order.addons && order.addons.length > 0 ? (
							<PunchupLineItemList
								items={order.addons.map((addon) => ({
									label: addon.name,
									count: addon.quantity,
								}))}
							/>
						) : (
							'-'
						)}
					</PunchupBreakdownCell>
				);
			default:
				return null;
		}
	};

	if (isLoading || !orders) {
		return (
			<tbody>
				<PunchupTableLoadingRow />
			</tbody>
		);
	}

	if (orders.pages[0].data.length === 0) {
		// Two distinct empty states: a search/filter that matched nothing vs. a show with no purchases yet.
		// Collapsing them would make a failed door lookup read as "this show sold nothing".
		const emptyText = hasActiveQuery
			? t('orders-empty-no-results', 'No transactions match that name, email, or order ID.')
			: t('orders-empty', 'No transactions yet — ticket purchases will appear here as they come in.');
		return (
			<tbody>
				<PunchupTableEmptyRow text={emptyText} />
			</tbody>
		);
	}

	return (
		<tbody>
			{orders.pages.map((page: Page) =>
				page.data.map((order: OrdersData) => (
					<PunchupDataRow key={order.id}>{visibleDefs.map((col) => renderCol(col, order))}</PunchupDataRow>
				))
			)}
		</tbody>
	);
};
